import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { GameService } from '../services/gameService';
import { formatNumber } from '../utils/formatters';

const { FiZap, FiCpu, FiTrendingUp, FiMousePointer } = FiIcons;

const VISUALS = {
  hero: "https://cdn.midjourney.com/25c40863-328f-48df-b6d6-d4d5ae06af89/0_2.webp",
  efficiency: "https://cdn.midjourney.com/f90b4a5c-89b9-49aa-9e38-60eb9e13d914/0_0.webp",
  automation: "https://cdn.midjourney.com/c8d5ae76-ae7c-4728-bae7-7894b2b1b1f7/0_0.webp"
};

export default function Upgrades() {
  const { state, actions } = useGame();

  const effCost = GameService.calculateEfficiencyCost(state.effLevel);
  const autoCost = GameService.calculateAutomationCost(state.autoLevel);
  const canBuyEfficiency = GameService.canAffordPurchase(state.seeds, effCost);
  const canBuyAutomation = GameService.canAffordPurchase(state.seeds, autoCost);

  const currentClickYield = GameService.calculateClickYield(state.effLevel);
  const nextClickYield = GameService.calculateClickYield(state.effLevel + 1);
  const currentAutoMultiplier = Math.pow(1.25, state.autoLevel - 1);
  const nextAutoMultiplier = Math.pow(1.25, state.autoLevel);

  const upgrades = [
    {
      id: 'efficiency',
      name: 'Harvest Efficiency',
      subtitle: 'Manual Yield Amplifier',
      description: 'Enhance your harvesting tools to gather more seeds with every interaction.',
      icon: FiZap,
      level: state.effLevel,
      cost: effCost,
      canAfford: canBuyEfficiency,
      action: actions.buyEfficiency,
      image: VISUALS.efficiency,
      currentValue: formatNumber(currentClickYield, 1),
      nextValue: formatNumber(nextClickYield, 1),
      unit: 'seeds / click',
      color: 'goldenrod',
      statLabel: 'Click Power'
    },
    {
      id: 'automation',
      name: 'System Optimization',
      subtitle: 'Passive Growth Multiplier',
      description: 'Refine your garden\'s automated systems to multiply all passive production.',
      icon: FiCpu,
      level: state.autoLevel,
      cost: autoCost,
      canAfford: canBuyAutomation,
      action: actions.buyAutomation,
      image: VISUALS.automation,
      currentValue: `${currentAutoMultiplier.toFixed(2)}x`,
      nextValue: `${nextAutoMultiplier.toFixed(2)}x`,
      unit: 'global multiplier',
      color: 'skyblue',
      statLabel: 'Auto Efficiency'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative h-64 rounded-3xl overflow-hidden shadow-2xl group">
        <div className="absolute inset-0 bg-purple-900/20 z-10 transition-colors duration-500 group-hover:bg-purple-900/10" />
        <img 
          src={VISUALS.hero} 
          alt="Upgrades Laboratory" 
          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2 text-shadow-sm">
            Research & Development
          </h1>
          <p className="text-white/90 font-dm-sans max-w-lg text-shadow-sm">
            Invest in advanced techniques to exponentially increase your garden's potential.
          </p>
        </div>
      </div>

      {/* Upgrades Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {upgrades.map((upgrade, index) => (
          <motion.div
            key={upgrade.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: index * 0.15 }}
          >
            <Card className="h-full flex flex-col overflow-hidden p-0 border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
              {/* Visual Header */}
              <div className="relative h-56 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-transparent to-transparent z-10" />
                <img 
                  src={upgrade.image} 
                  alt={upgrade.name} 
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" 
                />
                
                <div className="absolute top-4 right-4 z-20">
                  <div className="bg-black/40 backdrop-blur-md text-white px-3 py-1 rounded-full border border-white/10 text-sm font-bold shadow-lg">
                    Lvl {upgrade.level}
                  </div>
                </div>

                <div className="absolute bottom-4 left-4 z-20">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center bg-${upgrade.color}/20 text-${upgrade.color} backdrop-blur-sm`}>
                      <SafeIcon icon={upgrade.icon} className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-white/80 text-xs font-bold uppercase tracking-wider">{upgrade.subtitle}</span>
                  </div>
                  <h3 className="text-2xl font-cormorant font-bold text-white">{upgrade.name}</h3>
                </div>
              </div>

              {/* Content Body */}
              <div className="p-6 flex flex-col flex-grow bg-white relative">
                <p className="text-charcoal/70 text-sm mb-6 leading-relaxed">
                  {upgrade.description}
                </p>

                {/* Stat Comparison */}
                <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 flex items-center justify-between">
                  <div className="text-center">
                    <div className="text-xs text-charcoal/40 font-bold uppercase mb-1">Current</div>
                    <div className="text-lg font-bold text-charcoal">{upgrade.currentValue}</div>
                  </div>
                  
                  <div className="flex flex-col items-center px-4">
                    <span className="text-xs text-charcoal/40 font-medium mb-1">Improvement</span>
                    <SafeIcon icon={FiTrendingUp} className={`w-5 h-5 text-${upgrade.color}`} />
                  </div>

                  <div className="text-center">
                    <div className="text-xs text-charcoal/40 font-bold uppercase mb-1">Next Level</div>
                    <div className={`text-lg font-bold text-${upgrade.color}`}>{upgrade.nextValue}</div>
                  </div>
                </div>

                <div className="text-xs text-center text-charcoal/40 mb-6 -mt-2 font-medium">
                  {upgrade.unit}
                </div>

                {/* Action Footer */}
                <div className="mt-auto pt-6 border-t border-dashed border-gray-200">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-xs text-charcoal/50 font-bold uppercase tracking-wider mb-1">Research Cost</div>
                      <div className="text-2xl font-bold text-charcoal font-cormorant">
                        {formatNumber(upgrade.cost)}
                      </div>
                    </div>
                    <Button 
                      variant={upgrade.canAfford ? "primary" : "secondary"} 
                      disabled={!upgrade.canAfford} 
                      onClick={upgrade.action}
                      className="px-8 h-12 shadow-md hover:shadow-lg"
                    >
                      {upgrade.canAfford ? 'Upgrade' : 'Insufficient Seeds'}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Current Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <Card className="p-5 flex items-center gap-4 bg-gradient-to-r from-goldenrod/10 to-transparent border-goldenrod/20">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
            <SafeIcon icon={FiMousePointer} className="w-6 h-6 text-goldenrod" />
          </div>
          <div>
            <div className="text-sm text-charcoal/60 font-bold uppercase tracking-wider">Manual Harvest Power</div>
            <div className="text-2xl font-bold text-charcoal font-cormorant">{formatNumber(currentClickYield, 1)} <span className="text-sm font-sans font-normal text-charcoal/60">seeds/click</span></div>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 bg-gradient-to-r from-skyblue/10 to-transparent border-skyblue/20">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
            <SafeIcon icon={FiCpu} className="w-6 h-6 text-skyblue" />
          </div>
          <div>
            <div className="text-sm text-charcoal/60 font-bold uppercase tracking-wider">Automation Efficiency</div>
            <div className="text-2xl font-bold text-charcoal font-cormorant">{currentAutoMultiplier.toFixed(2)}x <span className="text-sm font-sans font-normal text-charcoal/60">multiplier</span></div>
          </div>
        </Card>
      </div>
    </div>
  );
}